import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../../components/Loading';
import {
   getCandidate,
   createCandidate,
   updateCandidate,
   uploadResume,
} from '../../../../services/hr/recruitment/candidatesApi';
import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../../styles/layout/ActionBar';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../styles/forms/Field';
import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';

import JobPositionSelect from '../components/JobPositionSelect';
import OriginSelect from '../components/OriginSelect';
import { GENDER_OPTIONS } from '../constants';
import { readApiError, recruitmentErrorMessage } from '../utils/apiError';
import {
   RESUME_ACCEPTED_MIME_TYPES,
   validateResumeFile,
} from '../utils/resume';

// ---------------------------------------------------------------------------
// RH · Recrutamento — cadastro/edição de candidato (handoff 0061 §4.3).
//
// Cria no BANCO_CURRICULOS (create) ou edita identidade/contato (edit). `stage`
// e `final_outcome` são READ-ONLY — NUNCA vão no body. CPF é armazenado só com
// dígitos. Erro: 422 com field → inline; resto → toast (readApiError/FC4).
// ---------------------------------------------------------------------------

const FIELD_ERROR_STYLE = {
   fontSize: 11,
   color: '#b71c1c',
   marginTop: 2,
};

const EMPTY_FORM = {
   full_name: '',
   gender: '',
   birth_date: '',
   desired_position_id: '',
   origin_id: '',
   phone: '',
   email: '',
   cpf: '',
   general_notes: '',
};

// birth_date pode vir como ISO datetime; o <input type="date"> quer só a data.
function toDateInput(value) {
   if (!value) return '';
   return String(value).substring(0, 10);
}

function onlyDigits(value) {
   return String(value ?? '').replace(/\D/g, '');
}

export default function CandidateForm() {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEditMode = Boolean(id);

   const [form, setForm] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);

   // Currículo (opcional) — só no cadastro; o upload exige o candidato já criado
   // (POST /candidates/:id/resume), então é feito num 2º passo após o create.
   const [resumeFile, setResumeFile] = useState(null);
   const fileInputRef = useRef(null);

   const handleResumeChange = (event) => {
      const file = event.target.files?.[0] ?? null;
      if (!file) {
         setResumeFile(null);
         return;
      }
      const validationError = validateResumeFile(file);
      if (validationError) {
         toast.error(validationError);
         event.target.value = '';
         setResumeFile(null);
         return;
      }
      setResumeFile(file);
   };

   const fetchCandidateForEdit = useCallback(async () => {
      setIsLoading(true);
      try {
         const candidate = await getCandidate(id);
         if (!candidate) {
            toast.error('Candidato não encontrado.');
            navigate('/hr/recruitment/pool');
            return;
         }
         setForm({
            full_name: candidate.full_name ?? '',
            gender: candidate.gender ?? '',
            birth_date: toDateInput(candidate.birth_date),
            desired_position_id: candidate.desired_position_id
               ? String(candidate.desired_position_id)
               : '',
            origin_id: candidate.origin_id ? String(candidate.origin_id) : '',
            phone: candidate.phone ?? '',
            email: candidate.email ?? '',
            cpf: candidate.cpf ?? '',
            general_notes: candidate.general_notes ?? '',
         });
      } catch (err) {
         toast.error(recruitmentErrorMessage(err, 'Erro ao carregar candidato.'));
         navigate('/hr/recruitment/pool');
      } finally {
         setIsLoading(false);
      }
   }, [id, navigate]);

   useEffect(() => {
      if (isEditMode) fetchCandidateForEdit();
   }, [isEditMode, fetchCandidateForEdit]);

   const updateField = (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setFieldErrors((prev) => {
         if (!prev[key]) return prev;
         const next = { ...prev };
         delete next[key];
         return next;
      });
   };

   function buildPayload() {
      // camelCase não se aplica aqui — os campos do candidato já são snake_case
      // e batem 1:1 com o backend. Os opcionais (`?` no contrato §4.3) são
      // OMITIDOS quando vazios — o Zod do backend espera `string`/número (ou o
      // campo ausente) e REJEITA `null` ("Tipo inválido. Esperado: string").
      // (Trade-off: no edit, esvaziar um campo não o "limpa" — só omite.)
      const payload = { full_name: form.full_name.trim() };
      if (form.gender) payload.gender = form.gender;
      if (form.birth_date) payload.birth_date = form.birth_date;
      if (form.desired_position_id) {
         payload.desired_position_id = Number(form.desired_position_id);
      }
      if (form.origin_id) payload.origin_id = Number(form.origin_id);
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      const cpfDigits = onlyDigits(form.cpf);
      if (cpfDigits) payload.cpf = cpfDigits;
      if (form.general_notes.trim()) {
         payload.general_notes = form.general_notes.trim();
      }
      return payload;
   }

   function handleSubmitError(err) {
      const { status, fieldErrors: apiFieldErrors } = readApiError(err);

      if (status === 409) {
         // conflito de unicidade (ex.: CPF já cadastrado) sem field mapeável.
         toast.error(recruitmentErrorMessage(err, 'Registro em conflito.'));
         return;
      }
      if (Object.keys(apiFieldErrors).length > 0) {
         setFieldErrors(apiFieldErrors);
         toast.error(
            err?.response?.data?.message ?? 'Verifique os campos destacados.',
         );
         return;
      }
      toast.error(recruitmentErrorMessage(err, 'Erro ao salvar candidato.'));
   }

   async function handleSubmit(event) {
      event.preventDefault();
      if (isSubmitting) return;

      const clientErrors = {};
      if (!form.full_name.trim()) {
         clientErrors.full_name = 'Nome completo é obrigatório.';
      }
      const cpfDigits = onlyDigits(form.cpf);
      if (cpfDigits && cpfDigits.length !== 11) {
         clientErrors.cpf = 'CPF deve ter 11 dígitos.';
      }
      if (Object.keys(clientErrors).length > 0) {
         setFieldErrors(clientErrors);
         toast.error('Verifique os campos destacados.');
         return;
      }

      setIsSubmitting(true);
      setFieldErrors({});
      try {
         const payload = buildPayload();
         if (isEditMode) {
            const { candidate, message } = await updateCandidate(id, payload);
            toast.success(message);
            navigate(`/hr/recruitment/candidates/${candidate?.id ?? id}`);
         } else {
            const { candidate, message } = await createCandidate(payload);

            // 2º passo: se o operador anexou um currículo, envia agora com o id
            // recém-criado. Se falhar, o candidato NÃO se perde — segue pra ficha
            // com um aviso para reenviar lá.
            let resumeFailed = false;
            if (resumeFile && candidate?.id) {
               try {
                  await uploadResume(candidate.id, resumeFile);
               } catch (uploadErr) {
                  resumeFailed = true;
               }
            }

            if (resumeFailed) {
               toast.error(
                  'Candidato criado, mas o currículo não foi enviado. Reenvie na ficha.',
               );
            } else {
               toast.success(message);
            }
            navigate(`/hr/recruitment/candidates/${candidate?.id}`);
         }
      } catch (err) {
         handleSubmitError(err);
      } finally {
         setIsSubmitting(false);
      }
   }

   const backTo = isEditMode
      ? `/hr/recruitment/candidates/${id}`
      : '/hr/recruitment/pool';

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>
                  {isEditMode ? 'Editar candidato' : 'Novo candidato'}
               </PageTitle>
               <PageSubtitle>
                  {isEditMode
                     ? 'Atualize os dados de identidade e contato do candidato.'
                     : 'Cadastre um candidato no banco de currículos.'}
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <GhostButton
                  type="button"
                  onClick={() => navigate(backTo)}
                  disabled={isSubmitting}
               >
                  Cancelar
               </GhostButton>
            </PageHeaderActions>
         </PageHeader>

         <form onSubmit={handleSubmit}>
            <Section>
               <SectionHeader>
                  <SectionTitle>Identidade</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-8">
                        <label htmlFor="candidate-name">
                           Nome completo
                           <RequiredMark>*</RequiredMark>
                        </label>
                        <input
                           id="candidate-name"
                           type="text"
                           maxLength={150}
                           value={form.full_name}
                           onChange={(e) =>
                              updateField('full_name', e.target.value)
                           }
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.full_name || undefined}
                        />
                        {fieldErrors.full_name && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.full_name}
                           </span>
                        )}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="candidate-gender">Gênero</label>
                        <select
                           id="candidate-gender"
                           value={form.gender}
                           onChange={(e) =>
                              updateField('gender', e.target.value)
                           }
                           disabled={isSubmitting}
                        >
                           <option value="">Não informado</option>
                           {GENDER_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="candidate-birth">
                           Data de nascimento
                        </label>
                        <input
                           id="candidate-birth"
                           type="date"
                           value={form.birth_date}
                           onChange={(e) =>
                              updateField('birth_date', e.target.value)
                           }
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.birth_date || undefined}
                        />
                        {fieldErrors.birth_date && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.birth_date}
                           </span>
                        )}
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="candidate-cpf">CPF</label>
                        <input
                           id="candidate-cpf"
                           type="text"
                           inputMode="numeric"
                           maxLength={14}
                           placeholder="Só números"
                           value={form.cpf}
                           onChange={(e) => updateField('cpf', e.target.value)}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.cpf || undefined}
                        />
                        {fieldErrors.cpf && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.cpf}
                           </span>
                        )}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            <Section>
               <SectionHeader>
                  <SectionTitle>Processo &amp; contato</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <FormGrid>
                     <Field className="col-6">
                        <label htmlFor="candidate-position">
                           Cargo pretendido
                        </label>
                        <JobPositionSelect
                           id="candidate-position"
                           value={form.desired_position_id}
                           onChange={(value) =>
                              updateField('desired_position_id', value)
                           }
                           disabled={isSubmitting}
                           invalid={!!fieldErrors.desired_position_id}
                        />
                        {fieldErrors.desired_position_id && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.desired_position_id}
                           </span>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="candidate-origin">
                           Origem da candidatura
                        </label>
                        <OriginSelect
                           id="candidate-origin"
                           value={form.origin_id}
                           onChange={(value) => updateField('origin_id', value)}
                           disabled={isSubmitting}
                           invalid={!!fieldErrors.origin_id}
                        />
                        {fieldErrors.origin_id && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.origin_id}
                           </span>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="candidate-phone">Telefone</label>
                        <input
                           id="candidate-phone"
                           type="tel"
                           maxLength={30}
                           value={form.phone}
                           onChange={(e) => updateField('phone', e.target.value)}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.phone || undefined}
                        />
                        {fieldErrors.phone && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.phone}
                           </span>
                        )}
                     </Field>

                     <Field className="col-6">
                        <label htmlFor="candidate-email">E-mail</label>
                        <input
                           id="candidate-email"
                           type="email"
                           maxLength={150}
                           value={form.email}
                           onChange={(e) => updateField('email', e.target.value)}
                           disabled={isSubmitting}
                           aria-invalid={!!fieldErrors.email || undefined}
                        />
                        {fieldErrors.email && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.email}
                           </span>
                        )}
                     </Field>

                     <Field className="col-12">
                        <label htmlFor="candidate-notes">Observações</label>
                        <textarea
                           id="candidate-notes"
                           rows={3}
                           value={form.general_notes}
                           onChange={(e) =>
                              updateField('general_notes', e.target.value)
                           }
                           disabled={isSubmitting}
                           placeholder="Anotações gerais sobre o candidato..."
                           aria-invalid={!!fieldErrors.general_notes || undefined}
                        />
                        {fieldErrors.general_notes && (
                           <span style={FIELD_ERROR_STYLE}>
                              {fieldErrors.general_notes}
                           </span>
                        )}
                     </Field>
                  </FormGrid>
               </SectionBody>
            </Section>

            {!isEditMode && (
               <Section>
                  <SectionHeader>
                     <SectionTitle>Currículo (opcional)</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     <FormGrid>
                        <Field className="col-12">
                           <label htmlFor="candidate-resume">
                              Arquivo (PDF, JPEG, PNG ou WEBP — máx. 10MB)
                           </label>
                           <input
                              id="candidate-resume"
                              ref={fileInputRef}
                              type="file"
                              accept={RESUME_ACCEPTED_MIME_TYPES}
                              onChange={handleResumeChange}
                              disabled={isSubmitting}
                           />
                           {resumeFile && (
                              <span
                                 style={{
                                    fontSize: 12,
                                    color: '#6b7280',
                                    marginTop: 4,
                                 }}
                              >
                                 {resumeFile.name}
                              </span>
                           )}
                        </Field>
                     </FormGrid>
                  </SectionBody>
               </Section>
            )}

            <ActionBar>
               <ActionBarLeft />
               <ActionBarRight>
                  <GhostButton
                     type="button"
                     onClick={() => navigate(backTo)}
                     disabled={isSubmitting}
                  >
                     Cancelar
                  </GhostButton>
                  <PrimaryButton type="submit" disabled={isSubmitting}>
                     {isSubmitting
                        ? 'Salvando...'
                        : isEditMode
                          ? 'Salvar alterações'
                          : 'Cadastrar candidato'}
                  </PrimaryButton>
               </ActionBarRight>
            </ActionBar>
         </form>
      </Container>
   );
}
